/**
 * Accès à `localStorage` qui ne peut jamais faire tomber le site.
 *
 * Cas réels rencontrés en production et couverts ici :
 *  - navigation privée Safari : `setItem` lève `QuotaExceededError` ;
 *  - cookies/stockage bloqués par la politique du navigateur : l'accès à
 *    `window.localStorage` lève une `SecurityError` dès la lecture de la
 *    propriété, avant même l'appel ;
 *  - contenu arbitraire déjà présent sous nos clés : tout ce qui sort du
 *    stockage est traité comme une donnée non fiable et re-validé.
 */

function store(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

export function readString(key: string, maxLength = 64): string | null {
  try {
    const raw = store()?.getItem(key);
    if (typeof raw !== 'string' || raw.length > maxLength) return null;
    return raw;
  } catch {
    return null;
  }
}

export function writeString(key: string, value: string): void {
  try {
    store()?.setItem(key, value);
  } catch {
    /* stockage plein ou interdit : le site marche sans. */
  }
}

/**
 * Lit un entier borné. Toute valeur absente, non numérique, négative,
 * fractionnaire ou au-delà de `max` retombe sur `fallback` : une clé trafiquée
 * ne peut pas produire un `NaN` qui remonterait jusqu'à l'affichage.
 */
export function readInt(key: string, fallback: number, max: number): number {
  const raw = readString(key, 24);
  if (raw === null) return fallback;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isSafeInteger(parsed) || parsed < 0 || parsed > max) return fallback;
  return parsed;
}
